import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Camera, Globe2, Loader2, PackageCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inputCls } from "../Login";
import { useAuth } from "../../context/AuthContext";
import { intlInvoiceHtml, openPrintWindow, printAllBoxLabels } from "../../lib/printDoc";

const SERVICE_NAMES = ["DHL", "FedEx", "UPS", "DPD", "INDIAFAST Intl", "Other"];
const SERVICE_TYPES = ["express", "dutypaid", "eco", "expedite", "self", "medicine", "document"];
const PAYMENT_MODES = [
  { id: "razorpay", label: "Razorpay" },
  { id: "cash", label: "Cash" },
  { id: "upi", label: "UPI" },
];

const emptyBox = () => ({ weight: "", length: "", width: "", height: "", items: [], photo: "" });
const boxCw = (b) => {
  const w = parseFloat(b.weight) || 0;
  const vol = b.length && b.width && b.height ? (parseFloat(b.length) * parseFloat(b.width) * parseFloat(b.height)) / 5000 : 0;
  return Math.max(w, vol, 0.1);
};
const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("if_token")}`;

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data.path;
}

export default function IntlBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [allCouriers, setAllCouriers] = useState([]);
  const [to, setTo] = useState({});
  const [serviceName, setServiceName] = useState("DHL");
  const [serviceType, setServiceType] = useState("express");
  const [shippingDate, setShippingDate] = useState("");
  const [sender, setSender] = useState({});
  const [mps, setMps] = useState(false);
  const [boxes, setBoxes] = useState([emptyBox()]);
  const [quotes, setQuotes] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [rateId, setRateId] = useState(null);
  const [received, setReceived] = useState("");
  const [paid, setPaid] = useState("");
  const [payMode, setPayMode] = useState("razorpay");
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    api.get("/rates/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
    api.get("/couriers").then((r) => setAllCouriers(r.data.couriers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setSender((p) => ({
      name: p.name ?? user.name ?? "", phone: p.phone ?? user.phone ?? "",
      address: p.address ?? user.address ?? "", pincode: p.pincode ?? user.pincode ?? "",
      city: p.city ?? user.city ?? "", state: p.state ?? user.state ?? "",
    }));
  }, [user]);

  const totalAll = boxes.reduce((s, b) => s + boxCw(b), 0);
  const heavy = totalAll > 10;
  const activeBoxes = heavy && mps ? boxes : boxes.slice(0, 1);
  const totalCw = activeBoxes.reduce((s, b) => s + boxCw(b), 0);
  const totalValue = activeBoxes.reduce((s, b) => s + b.items.reduce((x, it) => x + (parseFloat(it.value) || 0), 0), 0);
  const profit = (parseFloat(received) || 0) - (parseFloat(paid) || 0);

  const setToF = (k) => (e) => setTo({ ...to, [k]: e.target.value });
  const setSenderF = (k) => (e) => setSender({ ...sender, [k]: e.target.value });
  const fillSenderFromPincode = async () => {
    if (!/^\d{6}$/.test(sender.pincode || "")) return;
    try {
      const r = await api.get(`/public/pincode/${sender.pincode}`);
      if (r.data.city || r.data.state) {
        setSender((p) => ({ ...p, city: r.data.city || p.city, state: r.data.state || p.state }));
        toast.success(`Detected: ${r.data.city}, ${r.data.state}`);
      }
    } catch { /* pincode lookup is best-effort */ }
  };
  const setBox = (i, patch) => {
    const next = [...boxes];
    next[i] = { ...next[i], ...patch };
    setBoxes(next);
  };
  const addItem = (i) => setBox(i, { items: [...boxes[i].items, { name: "", quantity: 1, value: "" }] });
  const setItem = (bi, ii, patch) => {
    const items = [...boxes[bi].items];
    items[ii] = { ...items[ii], ...patch };
    setBox(bi, { items });
  };

  const getQuotes = async () => {
    if (!to.country) { toast.error("Select destination country first"); return; }
    setBusy(true);
    setQuotes(null);
    try {
      const res = await api.post("/rates/international", {
        destination_country: to.country,
        zipcode: to.zipcode || "",
        boxes: activeBoxes.map((b) => ({ weight: b.weight || 0.5, length: b.length || 0, width: b.width || 0, height: b.height || 0 })),
      });
      setQuotes(res.data);
      if (!allCouriers.length) api.get("/couriers").then((r) => setAllCouriers(r.data.couriers || [])).catch(() => {});
      if (!res.data.rates.length) toast.info("Price on request for this country — book now and our team will share the best price.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const pickQuote = (q) => {
    setSelectedQuote(q);
    setServiceName(q.courier_name);
    setServiceType(q.service);
    setPaid(q.price_on_request ? "" : String(q.customer_price));
    setRateId(q.rate_id || null);
  };

  const uploadProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await uploadFile(f);
      setProofs([...proofs, path]);
      toast.success("Payment proof uploaded");
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadBoxPhoto = async (i, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await uploadFile(f);
      setBox(i, { photo: path });
      toast.success(`Box ${i + 1} photo uploaded`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const submit = async () => {
    if (!sender.name || !sender.phone || !sender.address || !sender.pincode) { toast.error("Complete the Sender (From) details"); return; }
    if (!to.name || !to.country || !to.address || !to.phone) { toast.error("Complete the Deliver To fields"); return; }
    setBusy(true);
    try {
      const res = await api.post("/shipments/international", {
        sender,
        delivery: to,
        service_name: serviceName,
        service_type: serviceType,
        rate_id: rateId,
        shipping_date: shippingDate || null,
        boxes: activeBoxes.map((b) => ({
          weight: parseFloat(b.weight) || 0.5, length: parseFloat(b.length) || 0,
          width: parseFloat(b.width) || 0, height: parseFloat(b.height) || 0,
          items: b.items, photo: b.photo || null,
        })),
        total_value: totalValue,
        received_from_customer: parseFloat(received) || 0,
        paid_to_indiafast: parseFloat(paid) || 0,
        payment_mode: payMode,
        box_photos: activeBoxes.map((b) => b.photo).filter(Boolean),
        payment_proofs: proofs,
      });
      setBooked(res.data);
      toast.success(`International shipment created · ${res.data.invoice_number}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const printInvoice = async () => {
    try {
      const res = await api.get(`/shipments/${booked.id}/invoice`);
      openPrintWindow(`Invoice ${res.data.invoice_number}`, intlInvoiceHtml(res.data, booked), "a4");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (booked) {
    return (
      <div className="mx-auto max-w-lg space-y-6" data-testid="intl-success">
        <div className="rounded-2xl border border-emerald-200 bg-white p-10 text-center">
          <PackageCheck className="mx-auto h-14 w-14 text-emerald-500" />
          <h2 className="mt-4 font-heading text-2xl font-bold text-navy">International Shipment Created</h2>
          <p className="mt-2 font-mono text-sm text-slate-500">{booked.order_id}</p>
          <p className="mt-1 font-mono text-lg font-bold text-express">{booked.invoice_number}</p>
          <p className="mt-3 text-sm text-slate-600">{booked.service_name} · {booked.service_type} → {booked.destination_country}</p>
          <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm">
            <div className="flex justify-between"><span>Received from customer</span><b>₹{booked.received_from_customer}</b></div>
            <div className="flex justify-between"><span>Paid to INDIAFAST</span><b>₹{booked.paid_to_indiafast}</b></div>
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1">
              <span>Your profit</span>
              <b data-testid="intl-profit" className={profit >= 0 ? "text-emerald-600" : "text-red-500"}>₹{booked.profit}</b>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-400">Tracking number will be assigned by INDIAFAST admin after courier handover. No wallet deduction for international bookings.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={printInvoice} data-testid="intl-print-invoice-btn"
                  className="rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron">Print A4 Invoice</button>
          <button onClick={() => printAllBoxLabels(booked)} data-testid="intl-print-labels-btn"
                  className="rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">Print Box Labels ({booked.boxes.length})</button>
          <button onClick={() => navigate("/app/shipments")} data-testid="intl-view-shipments-btn"
                  className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy hover:bg-slate-100">My Shipments</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="intl-booking-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Book International Shipment</h1>
        <p className="mt-1 text-sm text-slate-500">Admin-priced lanes · per-box item lists · no wallet deduction</p>
      </div>

      {/* Sender */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Sender (From)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Name</label>
            <input required className={inputCls} data-testid="intl-sender-name" value={sender.name || ""} onChange={setSenderF("name")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</label>
            <input required className={inputCls} data-testid="intl-sender-phone" value={sender.phone || ""} onChange={setSenderF("phone")} /></div>
          <div className="col-span-2"><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Address</label>
            <input required className={inputCls} data-testid="intl-sender-address" value={sender.address || ""} onChange={setSenderF("address")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Pincode</label>
            <input required maxLength={6} className={inputCls} data-testid="intl-sender-pincode" placeholder="Auto-fills city & state"
                   value={sender.pincode || ""} onChange={setSenderF("pincode")} onBlur={fillSenderFromPincode} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">City</label>
            <input required className={inputCls} data-testid="intl-sender-city" value={sender.city || ""} onChange={setSenderF("city")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">State</label>
            <input required className={inputCls} data-testid="intl-sender-state" value={sender.state || ""} onChange={setSenderF("state")} /></div>
        </div>
      </div>

      {/* Deliver To */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Deliver To</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Recipient Name</label>
            <input required className={inputCls} data-testid="intl-to-name" value={to.name || ""} onChange={setToF("name")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Country</label>
            <select required className={inputCls} data-testid="intl-to-country" value={to.country || ""} onChange={setToF("country")}>
              <option value="">Select country…</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__other">Other (type below)</option>
            </select></div>
          {to.country === "__other" && (
            <div className="col-span-2"><input className={inputCls} data-testid="intl-to-country-custom" placeholder="Type country name" onChange={setToF("country")} /></div>
          )}
          <div className="col-span-2"><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Address</label>
            <input required className={inputCls} data-testid="intl-to-address" value={to.address || ""} onChange={setToF("address")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">City</label>
            <input className={inputCls} data-testid="intl-to-city" value={to.city || ""} onChange={setToF("city")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Zipcode</label>
            <input className={inputCls} data-testid="intl-to-zipcode" placeholder="e.g. 10001" value={to.zipcode || ""} onChange={setToF("zipcode")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone (international, + allowed)</label>
            <input className={inputCls} data-testid="intl-to-phone" placeholder="+1 555 123 4567" value={to.phone || ""} onChange={setToF("phone")} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email (opt.)</label>
            <input type="email" className={inputCls} value={to.email || ""} onChange={setToF("email")} /></div>
        </div>
      </div>

      {/* Service */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Service</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Service Name</label>
            <select className={inputCls} data-testid="intl-service-name" value={serviceName} onChange={(e) => setServiceName(e.target.value)}>
              {[...new Set([...(allCouriers.length ? allCouriers : SERVICE_NAMES), "Other"])].map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Shipping Date</label>
            <input type="date" className={inputCls} data-testid="intl-shipping-date" value={shippingDate} onChange={(e) => setShippingDate(e.target.value)} /></div>
        </div>
        <label className="mb-1 mt-4 block text-xs font-semibold uppercase tracking-wider text-slate-500">Service Type</label>
        <div className="flex flex-wrap gap-2" data-testid="intl-service-types">
          {SERVICE_TYPES.map((t) => (
            <button key={t} type="button" onClick={() => setServiceType(t)} data-testid={`intl-stype-${t}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors ${serviceType === t ? "bg-express text-white" : "border border-slate-300 text-slate-500 hover:border-express hover:text-express"}`}>
              {t === "dutypaid" ? "Duty Paid" : t}
            </button>
          ))}
        </div>
      </div>

      {/* Boxes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Packages & Items</h3>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {heavy ? (
            <>
              <span className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700">Heavy shipment (&gt;10 kg) — choose package type</span>
              <div className="flex rounded-full border border-slate-300 bg-white p-0.5">
                <button type="button" data-testid="intl-pkg-single" onClick={() => { setMps(false); setBoxes((b) => b.slice(0, 1)); }}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold ${!mps ? "bg-navy text-white" : "text-slate-500"}`}>Single Box</button>
                <button type="button" data-testid="intl-pkg-multi" onClick={() => setMps(true)}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold ${mps ? "bg-saffron text-white" : "text-slate-500"}`}>Multiple Boxes</button>
              </div>
            </>
          ) : (
            <span className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
              <Box className="h-3.5 w-3.5 text-saffron" /> Single Box Shipment (SPS) — up to 10 kg ships as one box
            </span>
          )}
        </div>

        <div className="mt-4 space-y-4">
          {activeBoxes.map((b, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`intl-box-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-heading text-sm font-bold text-navy">Box {i + 1} <span className="ml-2 rounded-full bg-saffron-light px-2 py-0.5 text-[10px] font-bold text-saffron" data-testid={`intl-box-${i}-billed`}>Billed {boxCw(b).toFixed(2)} kg</span></p>
                {heavy && mps && boxes.length > 1 && (
                  <button type="button" onClick={() => setBoxes(boxes.filter((_, j) => j !== i))} data-testid={`intl-box-${i}-remove`} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {[["weight", "Wt kg"], ["length", "L cm"], ["width", "W cm"], ["height", "H cm"]].map(([k, l]) => (
                  <div key={k}><label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">{l}</label>
                    <input type="number" step="0.1" min="0" className={inputCls} data-testid={`intl-box-${i}-${k}`} value={b[k]} onChange={(e) => setBox(i, { [k]: e.target.value })} /></div>
                ))}
              </div>
              {/* items */}
              <p className="mb-1 mt-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Items in this box (customs declaration)</p>
              {b.items.map((it, ii) => (
                <div key={ii} className="mb-2 grid grid-cols-12 items-end gap-2" data-testid={`intl-box-${i}-item-${ii}`}>
                  <div className="col-span-6"><input className={inputCls} placeholder="Item name" data-testid={`intl-box-${i}-item-${ii}-name`} value={it.name} onChange={(e) => setItem(i, ii, { name: e.target.value })} /></div>
                  <div className="col-span-2"><input type="number" min="1" className={inputCls} placeholder="Qty" data-testid={`intl-box-${i}-item-${ii}-qty`} value={it.quantity} onChange={(e) => setItem(i, ii, { quantity: e.target.value })} /></div>
                  <div className="col-span-3"><input type="number" min="0" className={inputCls} placeholder="Value ₹" data-testid={`intl-box-${i}-item-${ii}-value`} value={it.value} onChange={(e) => setItem(i, ii, { value: e.target.value })} /></div>
                  <button type="button" onClick={() => setBox(i, { items: b.items.filter((_, j) => j !== ii) })} className="col-span-1 pb-2 text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => addItem(i)} data-testid={`intl-box-${i}-add-item`}
                        className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron hover:text-saffron">
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </button>
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-navy hover:border-express hover:text-express" data-testid={`intl-box-${i}-photo-btn`}>
                  <Camera className="h-3.5 w-3.5" /> {b.photo ? "Photo added ✓" : "Box Photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBoxPhoto(i, e)} />
                </label>
              </div>
              {b.photo && <img src={fileUrl(b.photo)} alt={`Box ${i + 1}`} className="mt-2 h-16 w-16 rounded-lg object-cover" data-testid={`intl-box-${i}-photo-img`} />}
            </div>
          ))}
        </div>
        {heavy && mps && (
          <button type="button" onClick={() => setBoxes([...boxes, emptyBox()])} data-testid="intl-box-add"
                  className="mt-3 flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-navy hover:border-saffron hover:text-saffron">
            <Plus className="h-3.5 w-3.5" /> Add Box
          </button>
        )}
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600" data-testid="intl-total-billed">Total actual shipment weight (billed): {totalCw.toFixed(2)} kg</span>
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600" data-testid="intl-total-value">Declared value: ₹{totalValue.toLocaleString("en-IN")}</span>
        </div>
      </div>

      {/* Quote */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-navy">INDIAFAST Price (admin rate engine)</h3>
          <button type="button" onClick={getQuotes} disabled={busy} data-testid="intl-get-quotes-btn"
                  className="flex items-center gap-2 rounded-lg bg-express px-4 py-2 text-xs font-bold text-white hover:bg-navy disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe2 className="h-3.5 w-3.5" />} Get Price
          </button>
        </div>
        {quotes && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="intl-quotes">
            {quotes.rates.map((q, i) => (
              <button key={i} type="button" onClick={() => pickQuote(q)} data-testid={`intl-quote-${i}`}
                      className={`rounded-xl border-2 p-4 text-left transition-all ${selectedQuote === q ? "border-saffron bg-saffron-light" : "border-slate-200 hover:border-saffron/50"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading text-sm font-bold capitalize text-navy">{q.courier_name} · {q.service}</p>
                  <p className="font-heading text-xl font-extrabold text-navy">₹{q.customer_price.toLocaleString("en-IN")}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500">{q.transit_days}{q.notes ? ` · ${q.notes}` : ""}</p>
              </button>
            ))}
            {allCouriers.filter((cn) => !quotes.rates.some((q) => q.courier_name === cn)).map((cn, i) => (
              <button key={`por-${i}`} type="button"
                      onClick={() => pickQuote({ courier_name: cn, service: serviceType || "express", customer_price: 0, rate_id: null, price_on_request: true, transit_days: "" })}
                      data-testid={`intl-quote-por-${i}`}
                      className={`rounded-xl border-2 border-dashed p-4 text-left transition-all ${selectedQuote?.price_on_request && selectedQuote?.courier_name === cn ? "border-saffron bg-saffron-light" : "border-slate-200 hover:border-saffron/50"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading text-sm font-bold text-navy">{cn}</p>
                  <p className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">PRICE ON REQUEST</p>
                </div>
                <p className="mt-1 text-xs text-slate-500">Book now — our team will confirm the best price on WhatsApp</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Payment</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Received from Customer (₹)</label>
            <input type="number" min="0" className={inputCls} data-testid="intl-received" value={received} onChange={(e) => setReceived(e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Paid to INDIAFAST (₹)</label>
            <input type="number" min="0" className={inputCls} data-testid="intl-paid" value={paid} onChange={(e) => setPaid(e.target.value)} /></div>
        </div>
        <div className={`mt-3 rounded-xl p-3 text-sm font-bold ${profit >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="intl-profit-preview">
          Your profit: ₹{profit.toLocaleString("en-IN")} <span className="text-xs font-normal">(tracked separately — never added to wallet)</span>
        </div>
        <label className="mb-1 mt-4 block text-xs font-semibold uppercase tracking-wider text-slate-500">Payment Mode</label>
        <div className="flex gap-2" data-testid="intl-payment-modes">
          {PAYMENT_MODES.map((m) => (
            <button key={m.id} type="button" onClick={() => setPayMode(m.id)} data-testid={`intl-paymode-${m.id}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${payMode === m.id ? "bg-saffron text-white" : "border border-slate-300 text-slate-500 hover:border-saffron hover:text-saffron"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="mt-4 flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-xs font-bold text-navy hover:border-express hover:text-express" data-testid="intl-proof-upload-btn">
          <Camera className="h-3.5 w-3.5" /> Upload Payment Proof {proofs.length > 0 && `(${proofs.length} added)`}
          <input type="file" accept="image/*" className="hidden" onChange={uploadProof} />
        </label>
        <div className="mt-2 flex gap-2">
          {proofs.map((p, i) => <img key={i} src={fileUrl(p)} alt="proof" className="h-14 w-14 rounded-lg object-cover" data-testid={`intl-proof-${i}`} />)}
        </div>
      </div>

      <button onClick={submit} disabled={busy} data-testid="intl-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create International Shipment & Invoice
      </button>
    </div>
  );
}
